import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Truck, Mail, Phone, Download, Upload } from 'lucide-react';
import { fmt } from '../../lib/accountingStore';
import { apiVendors } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { apiVendors.list().then((d) => setVendors(d || [])).catch(() => setVendors([])); }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { try { await apiVendors.remove(id); load(); toast.success('Vendor removed'); } catch { toast.error('Failed'); } };
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-vendors">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Vendors</h2><p className="text-slate-500 text-sm">{vendors.length} vendors · {fmt(vendors.reduce((a, v) => a + (Number(v.balance) || 0), 0))} open A/P</p></div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-vendor-btn"><Plus className="w-4 h-4" />New Vendor</Button>
      </div>
      <div className="flex gap-2 -mt-2">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-3.5 h-3.5" />Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const parsed = parseCSV(await readFileAsText(f));
            const docs = parsed.map((r, i) => ({ id: r.ID || `V-${Date.now()}-${i}`, name: r.Name || '', email: r.Email || '', phone: r.Phone || '', balance: Number(r.Balance) || 0 }));
            try { await apiVendors.bulk(docs); load(); toast.success(`Imported ${docs.length} vendors`); } catch { toast.error('Import failed'); }
            e.target.value = '';
          }} /></label>
        <Button size="sm" variant="outline" onClick={() => { downloadFile('xpertpests-vendors.csv', toCSV(vendors, [{ key: 'id', header: 'ID' }, { key: 'name', header: 'Name' }, { key: 'email', header: 'Email' }, { key: 'phone', header: 'Phone' }, { key: 'balance', header: 'Balance' }])); toast.success('Exported vendors.csv'); }} className="gap-1.5 h-9"><Download className="w-3.5 h-3.5" />Export CSV</Button>
      </div>
      {vendors.length === 0 ? (
        <Card className="p-10 text-center border-slate-200"><Truck className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No vendors yet. Add suppliers you buy from to track bills and expenses.</p></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v) => (
            <Card key={v.id} className="pp-lift p-5 border-slate-200">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><Truck className="w-5 h-5" /></div>
                <Button size="sm" variant="ghost" onClick={() => onDelete(v.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
              <div className="font-bold text-slate-900 mt-3">{v.name}</div>
              <div className="text-xs text-slate-500 font-mono">{v.id}</div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{v.email || '—'}</div>
                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{v.phone || '—'}</div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-end">
                <div className="text-xs text-slate-500">Open Balance</div>
                <div className={`font-bold tabular-nums ${(Number(v.balance) || 0) > 0 ? 'text-red-600' : 'text-slate-500'}`}>{fmt(v.balance)}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <VendorDialog open={open} setOpen={setOpen} onSave={load} />
    </div>
  );
}

function VendorDialog({ open, setOpen, onSave }) {
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [phone, setPhone] = useState('');
  const submit = async () => {
    if (!name) { toast.error('Name required'); return; }
    try { await apiVendors.create({ id: `V-${Date.now().toString(36)}`, name, email, phone, balance: 0 }); onSave(); setOpen(false); setName(''); setEmail(''); setPhone(''); toast.success('Vendor added'); } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="vendor-dialog">
        <DialogHeader><DialogTitle>New Vendor</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3"><div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" /></div><div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" /></div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="vendor-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
